import { useMemo } from 'react';

// Optimized maps: store only points of font size change
// For intermediate values, the closest smaller or equal key is used
const FontSizeMapListDesktop = new Map<number, number>([
  [1, 56],
  [6, 48],
  [7, 42],
  [8, 36],
  [9, 30],
  [10, 28],
  [11, 24],
  [12, 22],
  [13, 20],
  [14, 18],
  [15, 16],
  [16, 14],
  [17, 12],
  [18, 10],
]);

const FontSizeMapListMobile = new Map<number, number>([
  [1, 32],
  [8, 26],
  [9, 22],
  [10, 20],
  [11, 18],
  [12, 16],
  [13, 14],
  [15, 12],
]);

/**
 * Находит ближайший меньший или равный ключ в отсортированной карте
 */
const findClosestKey = (
  map: Map<number, number>,
  target: number,
): number | undefined => {
  const keys = Array.from(map.keys()).sort((a, b) => a - b);

  if (keys.length === 0) {
    return undefined;
  }

  const firstKey = keys[0];
  if (firstKey === undefined) {
    return undefined;
  }

  if (target < firstKey) {
    return firstKey;
  }

  for (let i = keys.length - 1; i >= 0; i--) {
    const key = keys[i];
    if (key !== undefined && key <= target) {
      return key;
    }
  }

  return undefined;
};

/**
 * Hook for dynamic font size adjustment based on content length and device type
 * Returns font size from predefined maps based on text length
 * Uses optimized maps with search for the closest smaller or equal key
 */
export const useDynamicFontSize = (
  text: string,
  isMobile: boolean,
  fontSizeMaps: {
    mobile: Map<number, number>;
    desktop: Map<number, number>;
  } = {
    mobile: FontSizeMapListMobile,
    desktop: FontSizeMapListDesktop,
  },
) => {
  const fontSize = useMemo(() => {
    const fontSizeMap = isMobile ? fontSizeMaps.mobile : fontSizeMaps.desktop;
    const textLength = text.length;

    const mappedFontSize = fontSizeMap.get(textLength);
    if (mappedFontSize !== undefined) {
      return mappedFontSize;
    }

    const closestKey = findClosestKey(fontSizeMap, textLength);
    if (closestKey !== undefined) {
      const fontSize = fontSizeMap.get(closestKey);
      if (fontSize !== undefined) {
        return fontSize;
      }
    }

    const keys = Array.from(fontSizeMap.keys());
    if (keys.length > 0) {
      const maxKey = Math.max(...keys);
      const maxFontSize = fontSizeMap.get(maxKey);
      if (maxFontSize !== undefined) {
        return maxFontSize;
      }
    }

    return isMobile ? 12 : 10;
  }, [text, isMobile, fontSizeMaps]);

  return fontSize;
};
